// Ortsspecifik copy för fotosidorna.
//
// Syftet är att varje ort ska ha egen text — både i ortskorten på /foto och
// på landningssidan — i stället för samma mall med utbytt ortsnamn. Här bor
// också det citerbara kortsvaret (AEO) som används i schema per ort, så att
// varje ortskort får eget AEO/GEO-underlag och inte bara hubben.

export interface FotoCityCopy {
  /** Unik text i ortskortet på /foto — inte samma som sidans intro. */
  card: string;
  /** Näringslivsvinkel som används i landningssidans brödtext. */
  market: string;
  /** Citerbar fråga för AEO-schema per ort. */
  question: string;
  /** Citerbart svar (2–4 meningar) per ort. */
  answer: string;
  /** Extra sektioner på landningssidan för prioriterade orter. */
  sections?: { id: string; heading: string; body: string }[];
  /** Extra ortsspecifika frågor i FAQ + FAQPage-schema. */
  faq?: { q: string; a: string }[];
}

export const fotoCityCopy: Record<string, FotoCityCopy> = {
  stockholm: {
    card: "Vår tätaste marknad. Studiodag i city eller mobil studio på kontoret — 25–40 företagsporträtt hinns med på en dag när schemat är satt i förväg.",
    market:
      "Stockholm är vår tätaste marknad: börsbolag, techbolag i Kista och Sickla, konsultbyråer i innerstaden och logistik kring Arlanda. Det betyder korta inställelsetider, möjlighet att fotografera samma vecka och en fotograf som känner till vilka bildmanér som redan används i branschen.",
    question: "Vad kostar en företagsfotograf i Stockholm och hur snabbt kan ni komma?",
    answer:
      "Fenny Foto är företagsfotograf i Stockholm med studio i city och mobil studio som vi bygger i era lokaler. En halvdag med företagsporträtt räcker normalt till 15–20 personer, en heldag till 25–40 personer, och priset offereras per uppdrag utifrån tid, antal färdiga bilder, retusch och användningsrätt. Vi kan oftast fotografera inom en vecka i Storstockholm, inklusive Solna, Kista, Sickla och Arlanda-området, och levererar retuscherade bilder inom 3–5 arbetsdagar. Kontaktperson är Fenny, hey@fenny.se.",
    sections: [
      {
        id: "fotograf-stockholm",
        heading: "Fotograf Stockholm — så jobbar vi i huvudstaden",
        body: "Söker ni fotograf i Stockholm för ett helt kontor är logistiken viktigare än kameran. Vi lägger ett tidsschema med 10 minuter per person, bygger en ljussättning som håller hela dagen och fotograferar i ett rum ni ändå inte använder — det räcker med cirka tre gånger tre meter. För kampanj och miljöbilder rör vi oss i stället ute i verksamheten: kontoret på Södermalm, labbet i Solna, lagret i Jordbro eller ute på stan. Vi arbetar regelbundet i Innerstaden, Solna, Kista, Sickla, Södermalm och Arlanda-området, och restid inom tullarna ingår i offerten.",
      },
      {
        id: "stockholm-priser",
        heading: "Prisbild för fotografering i Stockholm",
        body: "Stockholmspriser skiljer sig inte från vår nationella prisbild — vi offererar per uppdrag i stället för timpris. Det som styr kostnaden är antal timmar på plats, hur många bilder som ska retuscheras, om vi behöver studio med hyrd lokal och hur bilderna ska användas. En porträttdag med hela ledningsgruppen, en kampanjdag med modeller och en eventbevakning ligger på tre helt olika nivåer, och ni får alltid ett fast pris innan dagen bokas.",
      },
    ],
    faq: [
      {
        q: "Har ni studio i Stockholm eller kommer ni till oss?",
        a: "Både och. Vi har tillgång till studio i Stockholm när ni vill ha ren, enhetlig bakgrund, och vi tar med mobil studio när det är enklare att samla personalen på kontoret. Valet påverkar priset marginellt — det som styr mest är hur många timmar vi behöver vara på plats.",
      },
      {
        q: "Hur snabbt kan ni boka in en fotografering i Stockholm?",
        a: "I Storstockholm kan vi oftast fotografera inom en vecka och vid brådskande behov inom ett par dagar. Vid större kampanjproduktioner med casting och plats­bokning bör ni räkna med två till tre veckors framförhållning.",
      },
    ],
  },
  goteborg: {
    card: "Västsverige med industri, fordon och tech. Vi kombinerar ofta porträttdag på kontoret med miljöbilder ute i produktionen på Hisingen eller i Mölndal.",
    market:
      "Göteborg och Västsverige domineras av fordonsindustri, logistik, techbolag på Lindholmen och en stark konsultsektor. Det märks i bildbehovet: porträtt som ska funka i både LinkedIn och offertunderlag, plus miljöbilder från verkstad, hamn eller labb som visar att ni faktiskt bygger något.",
    question: "Vilken fotograf i Göteborg kan fotografera både porträtt och industrimiljö?",
    answer:
      "Fenny Foto arbetar som fotograf i Göteborg med både företagsporträtt och miljöbilder ute i produktionen. En vanlig upplägg är förmiddag med porträtt i mobil studio på kontoret och eftermiddag med reportagebilder på Hisingen, i Mölndal eller på Lindholmen — samma dag, samma bildmanér och en enda offert. Vi fotograferar i Centrum, Lindholmen, Gårda, Mölndal, Hisingen och Landvetter, offererar per uppdrag och levererar retuscherade bilder inom 3–5 arbetsdagar. Kontaktperson är Fenny, hey@fenny.se.",
    sections: [
      {
        id: "fotograf-goteborg",
        heading: "Fotograf Göteborg — porträtt på kontoret, bilder ute i verksamheten",
        body: "När vi arbetar som fotograf i Göteborg är det oftast två saker som ska lösas samma dag: enhetliga porträtt på alla medarbetare och trovärdiga miljöbilder från den faktiska verksamheten. Vi riggar mobil studio i ett mötesrum, betar av personalen med tio minuter per person och går sedan ut i verkstaden, hamnen eller labbet med reportageljus. Skyddsutrustning och säkerhetsgenomgång är rutin — vi är vana vid industrimiljö och stör inte produktionen.",
      },
      {
        id: "goteborg-omraden",
        heading: "Var i Göteborg vi fotograferar",
        body: "Vi fotograferar regelbundet i Centrum, på Lindholmen, i Gårda, i Mölndal, på Hisingen och kring Landvetter. Restid inom Storgöteborg ingår i offerten. Behöver ni täcka flera anläggningar i Västsverige lägger vi upp en turné över två dagar i stället för separata tillfällen — det brukar sänka totalkostnaden märkbart.",
      },
    ],
    faq: [
      {
        q: "Kan ni fotografera i industrimiljö i Göteborg?",
        a: "Ja. Vi arbetar löpande i verkstäder, hamnmiljö och produktionsanläggningar i Västsverige, tar med egen belysning och följer er säkerhetsrutin. Vi behöver bara veta i förväg vilken skyddsutrustning som krävs och om något område inte får synas på bild.",
      },
      {
        q: "Reser ni till anläggningar utanför Göteborg?",
        a: "Ja, vi arbetar i hela Västra Götaland och åker gärna till flera anläggningar under samma resa. Restid utanför Storgöteborg specificeras separat i offerten.",
      },
    ],
  },
  malmo: {
    card: "Skåne och Öresund. Starkt fokus på e-handel, förpackning och produktbilder — ofta kombinerat med porträtt i Västra hamnen eller Hyllie.",
    market:
      "Malmö och Öresundsregionen har en tydlig e-handels- och konsumentprofil: varumärken som behöver produktbilder i volym, förpackningsbilder som håller för både webb och tryck, och porträtt som matchar en yngre visuell ton än traditionell industri.",
    question: "Vem fotograferar produkt- och företagsbilder i Malmö och Öresundsregionen?",
    answer:
      "Fenny Foto fotograferar i Malmö och Öresundsregionen med tyngdpunkt på produktfoto för e-handel, förpackningsbilder och företagsporträtt. Vi arbetar med kontrollerat studioljus för produkt i serie och mobil studio för porträtt på plats i Västra hamnen, Hyllie eller centrala Malmö, och täcker även Lund, Helsingborg och Köpenhamn. Priset offereras per uppdrag utifrån antal bilder, retuschnivå och användningsrätt, med leverans inom 3–5 arbetsdagar. Kontaktperson är Fenny, hey@fenny.se.",
    sections: [
      {
        id: "fotograf-malmo",
        heading: "Fotograf Malmö — produktbilder i volym och porträtt med samma manér",
        body: "Som fotograf i Malmö gör vi ofta två typer av uppdrag. Det första är produkt- och förpackningsfoto i serie: samma ljussättning, samma vinklar och en bildmall som gör att nya artiklar kan fotograferas senare och fortfarande matcha sortimentet i webbutiken. Det andra är företagsporträtt och miljöbilder på plats i Västra hamnen, Hyllie eller centrum. Behöver ni båda delarna lägger vi dem på samma produktionsdag och håller ett gemensamt bildmanér.",
      },
      {
        id: "malmo-oresund",
        heading: "Öresund: Malmö, Lund, Helsingborg och Köpenhamn",
        body: "Vi arbetar i hela Skåne och över bron. Malmö, Lund, Helsingborg och Köpenhamn ligger inom rimlig restid från samma bas, vilket gör det praktiskt att lägga två orter samma dag — exempelvis produktfoto i Malmö på morgonen och porträtt hos ett systerbolag i Lund efter lunch. Resa inom Skåne ingår normalt i offerten.",
      },
    ],
    faq: [
      {
        q: "Fotograferar ni produktbilder för e-handel i Malmö?",
        a: "Ja. Vi fotograferar produkt och förpackning i serie med fast ljussättning och levererar frilagda bilder eller miljöbilder efter behov. Vi dokumenterar uppsättningen så att kompletterande artiklar kan fotograferas senare och matcha samma manér.",
      },
      {
        q: "Kan ni fotografera i Lund, Helsingborg eller Köpenhamn?",
        a: "Ja, hela Öresundsregionen ingår i vårt arbetsområde. Resa inom Skåne ingår normalt i offerten; uppdrag i Köpenhamn specificeras separat.",
      },
    ],
  },
  uppsala: {
    card: "Life science, tech och offentlig verksamhet. Vi fotograferar labbmiljö och forskarporträtt med samma ljus som resten av bildbanken.",
    market:
      "Uppsala präglas av life science, universitetsnära bolag och offentlig verksamhet — miljöer där bildrättigheter, integritet och labbsäkerhet måste hanteras korrekt.",
    question: "Vilken fotograf i Uppsala kan fotografera labb- och forskningsmiljö?",
    answer:
      "Fenny Foto fotograferar i Uppsala för life science-bolag, techbolag och offentlig verksamhet: forskarporträtt, labbmiljö, miljöbilder och event. Vi tar hänsyn till renrumsrutiner, sekretess och vad som inte får synas på bild, och fotograferar i Centrum, Boländerna, Ultuna, Fyrislund och Knivsta. Offert per uppdrag och leverans inom 3–5 arbetsdagar. Kontaktperson är Fenny, hey@fenny.se.",
  },
  vasteras: {
    card: "Industri och Mälardalen. Egen bas i Västerås gör att vi kan fotografera med kort varsel i Finnslätten, Hacksta och Eskilstuna.",
    market:
      "Västerås och Mälardalen är industri- och energitungt: verkstad, automation och teknikbolag som behöver bilder både för rekrytering och för teknisk kommunikation.",
    question: "Finns det en företagsfotograf i Västerås som kan fotografera i industrimiljö?",
    answer:
      "Fenny har egen bas i Västerås och fotograferar företagsporträtt, industri- och verkstadsmiljö samt kampanjbilder i Mälardalen. Eftersom vi utgår från orten kan vi ofta boka in fotografering med kort varsel i Finnslätten, Hacksta, Erikslund, Eskilstuna och Enköping. Priset offereras per uppdrag och retuscherade bilder levereras inom 3–5 arbetsdagar. Kontaktperson är Fenny, hey@fenny.se.",
  },
  linkoping: {
    card: "Tech, försvar och universitetsnära bolag i Mjärdevi — porträtt och miljöbilder med hänsyn till sekretess på plats.",
    market:
      "Linköping har en tät koncentration av tech-, flyg- och försvarsnära bolag där sekretess ofta styr vad som får fotograferas.",
    question: "Vem fotograferar företagsbilder i Linköping?",
    answer:
      "Fenny Foto fotograferar företagsporträtt, miljöbilder och event i Linköping — särskilt för tech-, försvars- och universitetsnära bolag i Mjärdevi och Tornby. Vi går igenom i förväg vad som inte får synas på bild och levererar retuscherade bilder inom 3–5 arbetsdagar. Offert per uppdrag, kontakt: hey@fenny.se.",
  },
  norrkoping: {
    card: "Logistik, visualisering och industri. Ofta kombinerat med Linköping samma dag för att hålla nere restidskostnaden.",
    market:
      "Norrköping kombinerar logistik och industri med en växande visualiserings- och mediasektor.",
    question: "Kan man boka en företagsfotograf i Norrköping?",
    answer:
      "Ja, Fenny Foto fotograferar företagsporträtt, industri- och logistikmiljö samt event i Norrköping. Vi lägger gärna Norrköping och Linköping samma dag, vilket sänker restidskostnaden. Offert per uppdrag och leverans inom 3–5 arbetsdagar, kontakt: hey@fenny.se.",
  },
  orebro: {
    card: "Logistiknav i mitten av Sverige. Praktisk mötesplats när personal från flera orter ska fotograferas vid ett tillfälle.",
    market:
      "Örebro ligger logistiskt i mitten av landet och används ofta som samlingsplats när ett bolag med flera kontor ska fotografera all personal vid ett tillfälle.",
    question: "Fotograferar ni företagsporträtt i Örebro?",
    answer:
      "Ja. Fenny Foto fotograferar företagsporträtt, lager- och logistikmiljö samt event i Örebro. Orten fungerar bra som samlingsplats när personal från flera kontor ska fotograferas samma dag med identisk ljussättning. Offert per uppdrag, leverans inom 3–5 arbetsdagar, kontakt: hey@fenny.se.",
  },
  helsingborg: {
    card: "Nordvästra Skåne med handel och logistik. Kombineras enkelt med Malmö eller Lund under samma resa.",
    market:
      "Helsingborg har en tydlig handels-, förpacknings- och logistikprofil i nordvästra Skåne.",
    question: "Vem fotograferar företagsbilder i Helsingborg?",
    answer:
      "Fenny Foto fotograferar företagsporträtt, produktbilder och miljöbilder i Helsingborg och nordvästra Skåne. Vi kombinerar ofta Helsingborg med Malmö eller Lund samma resa. Offert per uppdrag, leverans inom 3–5 arbetsdagar, kontakt: hey@fenny.se.",
  },
  lund: {
    card: "Forskning, medtech och startups. Porträtt och labbmiljö med hänsyn till renrum och sekretess.",
    market:
      "Lund är forsknings- och medtechtungt med många snabbväxande bolag kring universitetet och forskningsanläggningarna.",
    question: "Vilken fotograf i Lund kan fotografera forskningsmiljö?",
    answer:
      "Fenny Foto fotograferar forskarporträtt, labb- och kontorsmiljö samt event i Lund. Vi anpassar oss efter renrums- och sekretessrutiner och stämmer av i förväg vad som får synas på bild. Offert per uppdrag, leverans inom 3–5 arbetsdagar, kontakt: hey@fenny.se.",
  },
  jonkoping: {
    card: "Tillverkning, möbel och logistik i Småland — miljöbilder från produktion är oftast det som saknas i bildbanken.",
    market:
      "Jönköpingsregionen är tillverknings- och logistiktung med starka familjeägda industribolag.",
    question: "Fotograferar ni företag i Jönköping?",
    answer:
      "Ja. Fenny Foto fotograferar företagsporträtt, produktions- och lagermiljö samt produktbilder i Jönköping och Småland. Offert per uppdrag och leverans inom 3–5 arbetsdagar, kontakt: hey@fenny.se.",
  },
  boras: {
    card: "Textil, e-handel och logistik. Produktfoto i serie är den vanligaste beställningen här.",
    market:
      "Borås är centrum för textil, e-handel och distanshandel, med stort behov av produktbilder i volym.",
    question: "Vem fotograferar produktbilder i Borås?",
    answer:
      "Fenny Foto fotograferar produkt- och plaggbilder i serie samt företagsporträtt i Borås. Vi sätter en bildmall så att nya artiklar kan fotograferas senare och matcha sortimentet. Offert per uppdrag, leverans inom 3–5 arbetsdagar, kontakt: hey@fenny.se.",
  },
  umea: {
    card: "Norrland med universitet, energi och offentlig sektor. Vi planerar resan så flera uppdrag ryms i samma vecka.",
    market:
      "Umeå har universitet, energibolag och offentlig sektor som huvudsakliga uppdragsgivare i norra Sverige.",
    question: "Kan ni fotografera i Umeå?",
    answer:
      "Ja. Fenny Foto fotograferar företagsporträtt, miljöbilder och event i Umeå. Vi planerar norrlandsresor så att flera uppdrag ryms under samma vecka, vilket delar upp restidskostnaden. Offert per uppdrag, leverans inom 3–5 arbetsdagar, kontakt: hey@fenny.se.",
  },
  lulea: {
    card: "Industri- och energiomställning i norr. Stora anläggningar kräver planerad ljussättning och säkerhetsgenomgång.",
    market:
      "Luleå och Norrbotten präglas av den gröna industriomställningen med stora anläggningsprojekt.",
    question: "Fotograferar ni industriprojekt i Luleå?",
    answer:
      "Ja. Fenny Foto fotograferar anläggnings- och industrimiljö, porträtt och event i Luleå och Norrbotten. Stora anläggningar planeras i förväg med ljussättning, säkerhetsgenomgång och tydlig bildlista. Offert per uppdrag, kontakt: hey@fenny.se.",
  },
  sundsvall: {
    card: "Bank, skogsindustri och IT längs Norrlandskusten — ofta porträtt för hela kontor på en dag.",
    market:
      "Sundsvall har finans, skogsindustri och IT som bärande näringar längs Norrlandskusten.",
    question: "Vem fotograferar företagsporträtt i Sundsvall?",
    answer:
      "Fenny Foto fotograferar företagsporträtt, kontors- och industrimiljö samt event i Sundsvall. Ett helt kontor hinns normalt med på en dag med mobil studio på plats. Offert per uppdrag, leverans inom 3–5 arbetsdagar, kontakt: hey@fenny.se.",
  },
  gavle: {
    card: "Industri, logistik och offentlig verksamhet — nära nog Stockholm för att fotograferas med kort varsel.",
    market:
      "Gävle ligger nära nog Stockholm för snabb inställelse och har industri, logistik och offentlig verksamhet som huvudkunder.",
    question: "Fotograferar ni i Gävle?",
    answer:
      "Ja. Fenny Foto fotograferar företagsporträtt, industri- och logistikmiljö samt event i Gävle. Närheten till Stockholm gör att vi ofta kan boka in fotografering med kort varsel. Offert per uppdrag, kontakt: hey@fenny.se.",
  },
  eskilstuna: {
    card: "Verkstad och automation i Mälardalen — täcks från vår bas i Västerås med kort restid.",
    market:
      "Eskilstuna är verkstads- och automationstungt och ligger nära vår bas i Västerås.",
    question: "Kan ni fotografera i Eskilstuna?",
    answer:
      "Ja. Vi utgår från Västerås och fotograferar företagsporträtt, verkstads- och industrimiljö samt event i Eskilstuna med kort restid. Offert per uppdrag, leverans inom 3–5 arbetsdagar, kontakt: hey@fenny.se.",
  },
  karlstad: {
    card: "Värmland med skog, papper och tjänstebolag — miljöbilder ute i produktionen är återkommande.",
    market:
      "Karlstad och Värmland har skogs- och pappersindustri samt en växande tjänstesektor.",
    question: "Vem fotograferar företagsbilder i Karlstad?",
    answer:
      "Fenny Foto fotograferar företagsporträtt, industri- och kontorsmiljö samt event i Karlstad och Värmland. Offert per uppdrag och leverans inom 3–5 arbetsdagar, kontakt: hey@fenny.se.",
  },
  vaxjo: {
    card: "Trä, energi och tillverkning i Småland — ofta kombinerat med Jönköping eller Halmstad samma resa.",
    market:
      "Växjö har trä-, energi- och tillverkningsbolag samt universitetsnära verksamhet.",
    question: "Fotograferar ni i Växjö?",
    answer:
      "Ja. Fenny Foto fotograferar företagsporträtt, produktions- och kontorsmiljö samt event i Växjö. Vi kombinerar gärna Växjö med Jönköping eller Halmstad under samma resa. Offert per uppdrag, kontakt: hey@fenny.se.",
  },
  halmstad: {
    card: "Halland med tillverkning, besöksnäring och handel — bilder som ska funka både kommersiellt och för rekrytering.",
    market:
      "Halmstad och Halland kombinerar tillverkning, handel och besöksnäring.",
    question: "Kan man anlita er som fotograf i Halmstad?",
    answer:
      "Ja. Fenny Foto fotograferar företagsporträtt, miljöbilder, produktfoto och event i Halmstad och Halland. Offert per uppdrag och leverans inom 3–5 arbetsdagar, kontakt: hey@fenny.se.",
  },
};

export const fotoCityCopyFor = (slug: string): FotoCityCopy | undefined =>
  fotoCityCopy[slug];
